import type { ReactElement } from "react";
import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";

import { countUniquePreparedSiteTags, MAX_SITE_TAGS_BULK_VALIDATION, parseSiteTagsBulkValue } from "./utils";

import {
    StyledBulkCapError,
    StyledBulkInputContainer,
    StyledBulkInputText,
    StyledCounter,
    StyledPositionRelative,
    StyledTextarea,
    StyledTextareaContainer,
} from "./styles";

type SiteTagsBulkInputProps = {
    value: string;
    onChange: (value: string) => void;
};

const SiteTagsBulkInput = ({ value, onChange }: SiteTagsBulkInputProps): ReactElement => {
    const { translate } = useTranslate();
    const [isFocused, setIsFocused] = useState(false);

    const parsedRawValue = useMemo(() => parseSiteTagsBulkValue(value), [value]);
    const preparedTagCount = useMemo(() => countUniquePreparedSiteTags(parsedRawValue), [parsedRawValue]);
    const isOverBulkLimit = preparedTagCount > MAX_SITE_TAGS_BULK_VALIDATION;

    const textTranslatable = leadsSearchFiltersI18nKeys.siteTagsBulkAddModalText();

    return (
        <StyledBulkInputContainer data-automation="site-tags-bulk-input">
            <StyledBulkInputText>{translate(textTranslatable.key, textTranslatable.replacements)}</StyledBulkInputText>

            <StyledPositionRelative>
                <StyledTextareaContainer focus={isFocused}>
                    <StyledTextarea
                        autoFocus
                        placeholder={translate(leadsSearchFiltersI18nKeys.siteTagsBulkAddModalPlaceholder())}
                        onChange={(event) => onChange(event.target.value)}
                        value={value}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        data-automation="site-tags-bulk-textarea"
                    />
                </StyledTextareaContainer>
                <StyledCounter isFull={preparedTagCount >= MAX_SITE_TAGS_BULK_VALIDATION}>
                    {Math.min(preparedTagCount, MAX_SITE_TAGS_BULK_VALIDATION)}/{MAX_SITE_TAGS_BULK_VALIDATION}{" "}
                    {translate(leadsSearchFiltersI18nKeys.siteTagsBulkAddModalCounter())}
                </StyledCounter>
            </StyledPositionRelative>
            {isOverBulkLimit ? (
                <StyledBulkCapError data-automation="site-tags-bulk-limit-error">
                    {translate(leadsSearchFiltersI18nKeys.siteTagsBulkAddModalMaxExceeded(), {
                        max: MAX_SITE_TAGS_BULK_VALIDATION,
                    })}
                </StyledBulkCapError>
            ) : null}
        </StyledBulkInputContainer>
    );
};

export default SiteTagsBulkInput;
